import type { TranslateFn } from "../../i18n";
import { MobileBottomTabs } from "../components/MobileBottomTabs";

/**
 * Mobile-only basics step.
 *
 * Layout:
 *   ┌─ title + subtitle
 *   │  METRIC | IMPERIAL toggle
 *   │
 *   │  HEIGHT          5ft 8in
 *   │  [-]  ─●──────────  [+]
 *   │  ─────────────────────
 *   │  WEIGHT         154 LBS
 *   │  [-]  ────●──────  [+]
 *   │  ─────────────────────
 *   │  AGE           30 YEARS
 *   │  [-]  ──●────────  [+]
 *   │
 *   │  ──── or ────
 *   │  📷 Upload a photo for AI sizing →
 *   │
 *   │  (spacer)
 *   ├─ Next button
 *   └─ MANUAL FIT | AI SCAN
 *
 * Stateless: parent owns form state. Sliders + step buttons all mutate
 * the same state — drag, tap +/-, or use the OS slider keyboard.
 */

function CameraSmallIcon({ size = 16 }: { size?: number }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" width={size} height={size}>
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
      <circle cx="12" cy="13" r="4" />
    </svg>
  );
}

function ChevronRight() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

function MinusIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" width="14" height="14">
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" width="14" height="14">
      <line x1="5" y1="12" x2="19" y2="12" />
      <line x1="12" y1="5" x2="12" y2="19" />
    </svg>
  );
}

interface BasicsStepMobileProps {
  hUnit: string;
  wUnit: string;
  isImperialMode: boolean;
  height: string;
  setHeight: (v: string) => void;
  heightFeet: string;
  setHeightFeet: (v: string) => void;
  heightInches: string;
  setHeightInches: (v: string) => void;
  weight: string;
  setWeight: (v: string) => void;
  age: string;
  setAge: (v: string) => void;
  switchToMetric: () => void;
  switchToImperial: () => void;
  onUploadPhoto: () => void;
  /** Tab bar callback — sets the parent step to "photo" */
  onSwitchToScan: () => void;
  /** Next button callback — advances the flow (parent owns handleNext) */
  onNext: () => void;
  /** Whether the Next button should be enabled */
  canProceed: boolean;
  /** Optional override label for the Next button (e.g. "Find My Best Fit"
   *  when an active profile is selected and we're skipping the wizard) */
  fastPathLabel?: string;
  /** Display name of the loaded profile — when set, shows a "Profile X loaded"
   *  banner with a "Start fresh AI sizing" link. */
  activeProfileName?: string | null;
  /** Deselect the active profile so the user can run a fresh AI sizing flow. */
  onStartFresh?: () => void;
  /** When true, hide the "Upload a photo for AI sizing" link AND swap the
   *  bottom Manual/Scan tab bar for nothing. Used when an active profile is
   *  selected — there's no point letting the user accidentally trip into the
   *  photo upload step when their saved profile already has everything we
   *  need to call /sizing/recommend. */
  hidePhotoOptions?: boolean;
  /** Accessories like bags only need height + weight, not age/body-shape questions. */
  showAge?: boolean;
  error: string;
  t: TranslateFn;
}

export function BasicsStepMobile({
  hUnit,
  wUnit,
  isImperialMode,
  height,
  setHeight,
  heightFeet,
  setHeightFeet,
  heightInches,
  setHeightInches,
  weight,
  setWeight,
  age,
  setAge,
  switchToMetric,
  switchToImperial,
  onUploadPhoto,
  onSwitchToScan,
  onNext,
  canProceed,
  fastPathLabel,
  activeProfileName,
  onStartFresh,
  hidePhotoOptions = false,
  showAge = true,
  error,
  t,
}: BasicsStepMobileProps) {
  // ── Derived values + ranges ──
  const heightCm = parseFloat(height) || 173;
  const heightInTotal = (parseFloat(heightFeet) || 5) * 12 + (parseFloat(heightInches) || 8);
  const ftDisplay = Math.floor(heightInTotal / 12);
  const inDisplay = heightInTotal % 12;
  const weightNum = parseFloat(weight) || (wUnit === "kg" ? 70 : 154);
  const ageNum = parseFloat(age) || 30;

  const wMinFix = wUnit === "kg" ? 30 : 66;
  const wMax = wUnit === "kg" ? 200 : 440;
  const hCmMin = 120, hCmMax = 220;
  const hInMin = 48, hInMax = 84;
  const ageMin = 13, ageMax = 100;

  const pctHCm = ((heightCm - hCmMin) / (hCmMax - hCmMin)) * 100;
  const pctHIn = ((heightInTotal - hInMin) / (hInMax - hInMin)) * 100;
  const pctW = ((weightNum - wMinFix) / (wMax - wMinFix)) * 100;
  const pctA = ((ageNum - ageMin) / (ageMax - ageMin)) * 100;

  // Gate the "Upload a photo for AI sizing" link on height + weight being
  // filled. Without these the backend can't build the MediaPipe cm-anchor
  // block and would fall back to visual-only estimation (inconsistent
  // across calls). Require the user to set both before advancing to the
  // photo step. Age is intentionally NOT required — it's optional for
  // body estimation.
  const heightFilled = isImperialMode
    ? parseFloat(heightFeet) > 0 || parseFloat(heightInches) > 0
    : parseFloat(height) > 0;
  const weightFilled = parseFloat(weight) > 0;
  const hasValidBasics = heightFilled && weightFilled;
  const heightDisplay = heightFilled
    ? (hUnit === "in" ? `${ftDisplay}ft ${inDisplay}in` : `${Math.round(heightCm)} cm`)
    : t("Set height");
  const weightDisplay = weightFilled ? `${Math.round(weightNum)} ${wUnit.toUpperCase()}` : t("Set weight");
  const ageDisplay = `${Math.round(ageNum)} ${t("YEARS")}`;

  // ── Step button handlers ──
  const setHeightTotalIn = (totalIn: number) => {
    const v = Math.max(hInMin, Math.min(hInMax, totalIn));
    setHeightFeet(String(Math.floor(v / 12)));
    setHeightInches(String(v % 12));
  };
  const incHeight = () => {
    if (hUnit === "in") setHeightTotalIn(heightInTotal + 1);
    else setHeight(String(Math.min(hCmMax, heightCm + 1)));
  };
  const decHeight = () => {
    if (hUnit === "in") setHeightTotalIn(heightInTotal - 1);
    else setHeight(String(Math.max(hCmMin, heightCm - 1)));
  };
  const incWeight = () => setWeight(String(Math.min(wMax, weightNum + 1)));
  const decWeight = () => setWeight(String(Math.max(wMinFix, weightNum - 1)));
  const incAge = () => setAge(String(Math.min(ageMax, ageNum + 1)));
  const decAge = () => setAge(String(Math.max(ageMin, ageNum - 1)));

  return (
    <div className="ps-bpm-root">
      {/* ── Title ── (subtitle hidden on mobile via CSS for a tighter header) */}
      <div className="ps-bpm-header">
        <h2 className="ps-bpm-title">{t("Body Measurements")}</h2>
        <p className="ps-bpm-subtitle">{t("Enter your details for a bespoke size suggestion")}</p>
      </div>

      {/* ── Active profile status ── */}
      {activeProfileName && (
        <div className="ps-bp-profile-card">
          <div className="ps-bp-profile-card-copy">
            <span className="ps-bp-profile-card-eyebrow">{t("Active Profile")}</span>
            <strong>{activeProfileName}</strong>
            <span>{t("Using saved measurements for this fit.")}</span>
          </div>
          {onStartFresh && (
            <button type="button" className="ps-bp-profile-card-action" onClick={onStartFresh}>
              {t("Start Fresh")}
            </button>
          )}
        </div>
      )}

      {/* ── Metric / Imperial toggle ── */}
      <div className="ps-bpm-toggle">
        <button
          type="button"
          className={`ps-bpm-toggle-btn${!isImperialMode ? " ps-active" : ""}`}
          onClick={switchToMetric}
        >
          {t("Metric")}
        </button>
        <button
          type="button"
          className={`ps-bpm-toggle-btn${isImperialMode ? " ps-active" : ""}`}
          onClick={switchToImperial}
        >
          {t("Imperial")}
        </button>
      </div>

      {/* ── Height / Weight / Age slider rows ── */}
      <div className="ps-bpm-fields">
        {/* HEIGHT */}
        <div className="ps-bpm-row">
          <div className="ps-bpm-row-top">
            <span className="ps-bpm-label">{t("HEIGHT")}</span>
            <span className={`ps-bpm-value-display${heightFilled ? "" : " ps-bpm-value-empty"}`}>
              {heightDisplay}
            </span>
          </div>
          <div className="ps-bpm-slider-row">
            <button type="button" className="ps-bpm-step-btn" onClick={decHeight} aria-label={t("Decrease")}>
              <MinusIcon />
            </button>
            {hUnit === "in" ? (
              <input
                type="range" className="ps-bpm-slider"
                min={hInMin} max={hInMax} step={1} value={heightInTotal}
                onChange={e => setHeightTotalIn(parseInt(e.target.value, 10))}
                style={{ ["--ps-pct" as any]: `${pctHIn}%` }}
              />
            ) : (
              <input
                type="range" className="ps-bpm-slider"
                min={hCmMin} max={hCmMax} step={1} value={heightCm}
                onChange={e => setHeight(e.target.value)}
                style={{ ["--ps-pct" as any]: `${pctHCm}%` }}
              />
            )}
            <button type="button" className="ps-bpm-step-btn" onClick={incHeight} aria-label={t("Increase")}>
              <PlusIcon />
            </button>
          </div>
        </div>

        {/* WEIGHT */}
        <div className="ps-bpm-row">
          <div className="ps-bpm-row-top">
            <span className="ps-bpm-label">{t("WEIGHT")}</span>
            <span className={`ps-bpm-value-display${weightFilled ? "" : " ps-bpm-value-empty"}`}>{weightDisplay}</span>
          </div>
          <div className="ps-bpm-slider-row">
            <button type="button" className="ps-bpm-step-btn" onClick={decWeight} aria-label={t("Decrease")}>
              <MinusIcon />
            </button>
            <input
              type="range" className="ps-bpm-slider"
              min={wMinFix} max={wMax} step={1} value={weightNum}
              onChange={e => setWeight(e.target.value)}
              style={{ ["--ps-pct" as any]: `${pctW}%` }}
            />
            <button type="button" className="ps-bpm-step-btn" onClick={incWeight} aria-label={t("Increase")}>
              <PlusIcon />
            </button>
          </div>
        </div>

        {showAge && (
          <div className="ps-bpm-row">
            <div className="ps-bpm-row-top">
              <span className="ps-bpm-label">{t("AGE")}</span>
              <span className="ps-bpm-value-display">{ageDisplay}</span>
            </div>
            <div className="ps-bpm-slider-row">
              <button type="button" className="ps-bpm-step-btn" onClick={decAge} aria-label={t("Decrease")}>
                <MinusIcon />
              </button>
              <input
                type="range" className="ps-bpm-slider"
                min={ageMin} max={ageMax} step={1} value={ageNum}
                onChange={e => setAge(e.target.value)}
                style={{ ["--ps-pct" as any]: `${pctA}%` }}
              />
              <button type="button" className="ps-bpm-step-btn" onClick={incAge} aria-label={t("Increase")}>
                <PlusIcon />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── "or" divider + minimal upload link (no background card) ──
          Hidden when an active profile is loaded — see hidePhotoOptions.
          Always enabled: photo upload is independent of the manual height +
          weight inputs. The vision model can size from the photo alone. */}
      {!hidePhotoOptions && (
        <>
          <div className="ps-bpm-or">
            <span>{t("or")}</span>
          </div>
          <button
            type="button"
            className="ps-bpm-upload-link"
            onClick={onUploadPhoto}
          >
            <CameraSmallIcon />
            <span>{t("Upload a photo for AI sizing")}</span>
            <ChevronRight />
          </button>
        </>
      )}

      {error && <p className="ps-bpm-error">{error}</p>}

      {/* ── Spacer pushes the bottom action area to the bottom ── */}
      <div className="ps-bpm-spacer" />

      {/* ── Bottom action: Next CTA + mode tabs ──
          The Manual/Scan tab bar is hidden when a profile is loaded — there
          is no other "mode" the user can switch to. */}
      <div className="ps-bpm-bottom">
        <button
          type="button"
          className="ps-bpm-next-btn"
          onClick={onNext}
          disabled={!canProceed}
        >
          {fastPathLabel || t("Next")}
        </button>
        {!hidePhotoOptions && (
          <MobileBottomTabs
            mode="manual"
            onSwitchToManual={() => { /* already on manual */ }}
            onSwitchToScan={onSwitchToScan}
            t={t}
          />
        )}
      </div>
    </div>
  );
}
